import BaseFormPresenter from "../../../../components/common/BaseFormPresenter";
import { findObjectUseCase } from "../../../../usecases/object";

const HIERARCHY_FIELDS = [
  "region",
  "office",
  "division",
  "section",
  "unit",
  "cluster",
];

const DUPLICATE_DEPARTMENT_HEAD_MESSAGE =
  "A Department Head is already assigned to this organizational structure. Please remove or replace the existing Department Head before assigning a new one.";

class DepartmentHeadFormPresenter extends BaseFormPresenter {
  onChangeObject(object) {
    this.change = object;
    this.object = object;
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = {
        include: ["selectedSupervisor"],
      };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();
        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  normalizeHierarchyValue(value) {
    return value || "";
  }

  isSameHierarchy(record, data) {
    return HIERARCHY_FIELDS.every(
      (field) =>
        this.normalizeHierarchyValue(record[field]) ===
        this.normalizeHierarchyValue(data[field]),
    );
  }

  async hasExistingDepartmentHead(collection, data, currentId) {
    const where = {};
    HIERARCHY_FIELDS.forEach((field) => {
      const value = this.normalizeHierarchyValue(data[field]);
      if (value) {
        where[field] = value;
      }
    });

    if (currentId) {
      where.id = { $ne: currentId };
    }

    const existingHeads = await findObjectUseCase().execute(collection, {
      where,
      keys: ["id", ...HIERARCHY_FIELDS],
    });

    return existingHeads.some((record) => this.isSameHierarchy(record, data));
  }

  updateEmployeeProgress(savedCount, totalCount) {
    this.view.setState({
      savedCount,
      totalCount,
      saveProgress:
        totalCount === 0 ? 100 : Math.round((savedCount / totalCount) * 100),
      progressMessage:
        totalCount === 0
          ? "No employees need to be updated"
          : `Updating ${savedCount} of ${totalCount} employees...`,
    });
  }

  getSavePayload() {
    const { object, selectedSupervisor } = this.view.state;
    const data = {};

    HIERARCHY_FIELDS.forEach((field) => {
      data[field] = this.view.state[field] || "";
    });

    if (selectedSupervisor) {
      data.selectedSupervisor = selectedSupervisor;
    }
    if (object?.id) {
      data.id = object.id;
    }

    return data;
  }

  async save() {
    this.view.setState({ progress: true });
    const { object, filteredEmp } = this.view.state;
    const collection = this.view.getCollectionName();
    const data = this.getSavePayload();

    const employeesToUpdate = filteredEmp || [];
    const totalCount = employeesToUpdate.length;

    this.view.setState({
      showProgressModal: true,
      saveProgress: 0,
      savedCount: 0,
      totalCount,
      progressMessage:
        totalCount === 1
          ? "1 employee needs to be updated"
          : `${totalCount} employees need to be updated`,
    });

    try {
      const alreadyAssigned = await this.hasExistingDepartmentHead(
        collection,
        data,
        object?.id,
      );
      if (alreadyAssigned) {
        this.view.setState({ showProgressModal: false, progress: false });
        this.showError(DUPLICATE_DEPARTMENT_HEAD_MESSAGE);
        return;
      }
    } catch (error) {
      this.view.setState({ showProgressModal: false, progress: false });
      this.showError(error.message || error);
      return;
    }

    try {
      let savedCount = 0;
      for (const emp of employeesToUpdate) {
        try {
          await this.upsertUseCase.execute("employees", {
            id: emp.id,
            supervisor: { id: data.selectedSupervisor.id },
          });
        } catch (e) {
          throw new Error(e.message || e);
        }
        savedCount += 1;
        this.updateEmployeeProgress(savedCount, totalCount);
      }

      await this.upsertUseCase.execute(collection, data);
      this.updateEmployeeProgress(totalCount, totalCount);
      await new Promise((resolve) => setTimeout(resolve, 400));
      this.view.setState({ showProgressModal: false });
      this.view.showSuccess("Success");
      this.view.navigateBack();
    } catch (error) {
      this.view.setState({ showProgressModal: false, progress: false });
      this.showError(error.message || error);
    } finally {
      this.view.setState({ progress: false, showProgressModal: false });
    }
  }
}

export default DepartmentHeadFormPresenter;
